import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { NV_PATHS, NvAccessDialog, NvAlertDialog, NvButton, NvErrorState, NvFinderCardSkeleton, NvSearchBar, NvToggleRow } from "@/components/navigator";
import { useAuth } from "@/contexts/auth-context";
import { useAccessGate } from "@/hooks/use-access-gate";
import { useRequest } from "@/hooks/use-request";
import { getApiError, getApiErrorMessage } from "@/lib/api-error";
import {
  EMPTY_FILTERS,
  FINDER_INITIAL_VIEW,
  hasFinderConditions,
  paramsFromQuery,
  queryFromParams,
  searchAgents,
  setMark,
  type FinderCard,
  type FinderFilterKey,
  type FinderQuery,
  type FinderRelaxSuggestion,
  type FinderResult,
  type FinderSort,
} from "@/services/navigator/finder-service";
import { getSearchSuggestions } from "@/services/navigator/home-service";
import { LockedArea, TrendingPanel, useLoginRedirect } from "../shared/agent-panels";
import { EmptyResult, FINDER_PLACEHOLDER, FilterRow, GuideStart, InterpretationBar, MoreButton, ResultCard, SectionTitle, SelectedBar, SortMenu, SuggestedTerms, type SelectedChip } from "./finder-parts";

// F01 Agent Finder (Figma ver.7: 847:49745 결과 · 847:49982 비회원 · 847:50233 결과 없음 · 847:50418 가이드형 · 847:57694 전체 목록형 · 847:51770 · 847:52187 더보기 · 접기).
// 검색어 · 필터 · 정렬은 URL에 남겨요(링크 공유 · 뒤로가기). 새 검색은 해석된 산업 · 업무를 필터에 자동 선택(ai=1 → 서버 auto).
// 조건 영역은 두 줄: "AI 해석"(✦ 칩) / "선택한 조건"(직접 고른 필터) (10/13 제안 09).
// 추천은 임계치를 넘은 전부(타이틀 옆 전체 개수)를 처음 10개 + [더보기] 10개씩 보이고, 마지막 묶음 뒤 [접기](slide 33 · 35).
// △(비회원 · 승인 대기 · 공급자)는 검색어 · 해석 칩 · 필터만 보이고 결과(더보기 포함) · 패널은 Blur + 로그인 유도 카드 한 번, 조작하면 이용 권한 안내 (slide 36 · 제안 21).
// 검색어 · 필터가 없으면 초기 진입: 기본은 "검색 시작 가이드"(전체 초기화 뒤 빈 화면 포함, slide 32 6-1),
//   FINDER_INITIAL_VIEW(finder-service.ts)를 "list"로 바꾸면 "전체 에이전트 N · 추천 순" 목록(칩 · 토글에 개수, 제안 16).

const PAGE = 10;
const TOGGLES = [
  { key: "certified", label: "Certified" },
  { key: "free_trial", label: "Free Trial" },
  { key: "on_premise", label: "On-Premise" },
] as const;
const FALLBACK_NOTICE = "일시적 지연이 발생하여 일반 키워드 검색 결과를 표시합니다";

export function NavigatorAgentFinderPage() {
  const { accessLevel, isLoading: authLoading } = useAuth();
  const gate = useAccessGate();
  const goLogin = useLoginRedirect();
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => queryFromParams(params), [params]);
  const auto = params.get("ai") === "1";
  const key = params.toString();
  const { denied } = gate;
  const initial = !hasFinderConditions(query);
  const isGuide = initial && FINDER_INITIAL_VIEW === "guide";
  const isAll = initial && FINDER_INITIAL_VIEW === "list";

  const [draft, setDraft] = useState(query.q ?? "");
  const [result, setResult] = useState<FinderResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 첫 쪽(result.recommended) 뒤에 이어 받은 추천과, 지금 보이게 하려는 개수(더보기 +10 · 접기 → 10)
  const [more, setMore] = useState<FinderCard[]>([]);
  const [visible, setVisible] = useState(PAGE);
  const [loadingMore, setLoadingMore] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0); // [다시 시도]
  const skipKey = useRef<string | null>(null);
  // The "더보기" request in flight. It belongs to the conditions it was started for, so a new search cancels it.
  const moreRequest = useRef<AbortController | null>(null);

  // 초기 진입 가이드의 "추천 검색어"는 HOME과 같은 API예요(안 불러와지면 줄만 안 보여요)
  const suggestionsRequest = useRequest((signal) => getSearchSuggestions(signal), [accessLevel], { enabled: isGuide && !authLoading, keepPreviousData: true });
  const suggestions = suggestionsRequest.error ? [] : (suggestionsRequest.data?.items ?? []);

  useEffect(() => setDraft(query.q ?? ""), [query.q]);
  useEffect(() => () => moreRequest.current?.abort(), []);

  useEffect(() => {
    if (authLoading) return;
    if (skipKey.current === key) {
      skipKey.current = null;
      return;
    }
    // A "더보기" still running for the previous conditions must not land on this search
    moreRequest.current?.abort();
    setLoadingMore(false);
    const controller = new AbortController();
    setLoading(true);
    // 가이드 화면은 필터 줄(건수)만 필요해서 카드는 1개만 받아요
    searchAgents(query, { auto, limit: isGuide ? 1 : PAGE, signal: controller.signal })
      .then((found) => {
        if (controller.signal.aborted) return;
        setResult(found);
        setVisible(PAGE);
        setMore([]);
        setError(null);
        if (auto) {
          // 해석으로 채운 필터를 URL에 맞춰요 — 같은 결과라 다시 부르지 않아요
          const next = paramsFromQuery({ ...found.filters, q: query.q, sort: query.sort });
          skipKey.current = next.toString();
          setParams(next, { replace: true });
        }
      })
      .catch((err) => !controller.signal.aborted && setError(getApiErrorMessage(err)))
      .finally(() => !controller.signal.aborted && setLoading(false));
    return () => {
      controller.abort();
      moreRequest.current?.abort();
    };
  }, [key, authLoading, accessLevel, attempt]);

  const block = () => gate.show();
  const apply = (patch: Partial<FinderQuery>) => {
    if (denied) return block();
    setParams(paramsFromQuery({ ...query, ...patch }));
  };
  const submit = (q: string) => {
    if (denied) return block();
    setParams(paramsFromQuery({ ...EMPTY_FILTERS, q, sort: query.sort }, { ai: "1" }));
  };

  const facets = result?.facets;
  const detailParent = useMemo(() => new Map((facets?.industry_detail ?? []).map((f) => [f.code, f.parent ?? ""])), [facets]);
  const toggleList = (field: FinderFilterKey, code: string) => {
    const current = query[field];
    if (current.includes(code)) {
      const patch: Partial<FinderQuery> = { [field]: current.filter((c) => c !== code) };
      // 대분류를 풀면 그 아래 세부도 같이 풀어요
      if (field === "industry") patch.industry_detail = query.industry_detail.filter((d) => detailParent.get(d) !== code);
      apply(patch);
    } else {
      apply({ [field]: [...current, code] });
    }
  };
  // 결과 없음의 [조건 초기화] = 선택한 필터만 해제(검색어는 그대로, slide 38 ②)
  const resetFilters = () => apply({ ...EMPTY_FILTERS });
  // 결과 없음의 "○○ 빼기 → N개": 그 조건 하나만 풀어요
  const relax = (suggestion: FinderRelaxSuggestion) => {
    if (suggestion.code) toggleList(suggestion.kind as FinderFilterKey, suggestion.code);
    else apply({ [suggestion.kind]: false } as Partial<FinderQuery>);
  };
  // 선택한 조건 바의 [↺ 전체 초기화] = 필터 + 검색어까지 모두 초기화하고 검색 결과 영역은 빈 화면(검색 시작 가이드, slide 32 6-1)
  const resetAll = () => {
    if (denied) return block();
    setParams(new URLSearchParams());
  };

  const labelOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of [...(facets?.industry ?? []), ...(facets?.industry_detail ?? []), ...(facets?.value_chain ?? [])]) map.set(f.code, f.label);
    return map;
  }, [facets]);

  const selectedChips: SelectedChip[] = [
    ...(["industry", "industry_detail", "value_chain"] as const).flatMap((field) =>
      query[field].map((code) => ({ key: `${field}:${code}`, label: labelOf.get(code) ?? code, onRemove: () => toggleList(field, code) })),
    ),
    ...TOGGLES.filter((t) => query[t.key]).map((t) => ({ key: t.key, label: t.label, onRemove: () => apply({ [t.key]: false }) })),
  ];

  const chips = (result?.interpretation ?? []).map((item, i) => ({ key: String(i), type: item.type, label: item.label, item }));
  // 해석으로 고른 조건의 코드 — 결과 없음 제안 칩에서 ✦ 를 붙이는 데 써요
  const aiCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const item of result?.interpretation ?? []) for (const list of Object.values(item.filter ?? {})) list?.forEach((c) => codes.add(c));
    return codes;
  }, [result]);
  const removeChip = (chipKey: string) => {
    if (denied) return block();
    const item = chips.find((c) => c.key === chipKey)?.item;
    if (!item) return;
    if (item.filter) {
      const patch: Partial<FinderQuery> = {};
      for (const [field, codes] of Object.entries(item.filter) as [FinderFilterKey, string[]][]) {
        // 대분류는 세부를 함께 고른 해석이면 세부만 풀어요
        const drop = field === "industry" && item.filter.industry_detail?.length ? [] : codes;
        patch[field] = query[field].filter((c) => !drop.includes(c));
      }
      apply(patch);
    } else if (query.q) {
      // 키워드 칩: 그 낱말을 뺀 검색어로 다시 찾아요 (칩 삭제 시 남은 조건으로 재검색, slide 32)
      const q = query.q.replace(new RegExp(item.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), " ").replace(/\s+/g, " ").trim();
      apply({ q: q || null });
    }
  };

  const toggleBookmark = async (card: FinderCard, next: boolean) => {
    if (denied) return block();
    const patchCard = (on: boolean) =>
      setResult((r) =>
        r && {
          ...r,
          recommended: r.recommended.map((c) => (c.id === card.id ? { ...c, is_bookmarked: on } : c)),
          similar: r.similar.map((c) => (c.id === card.id ? { ...c, is_bookmarked: on } : c)),
        },
      );
    patchCard(next);
    setMore((list) => list.map((c) => (c.id === card.id ? { ...c, is_bookmarked: next } : c)));
    try {
      await setMark("bookmarks", card.id, next);
    } catch (err) {
      patchCard(!next);
      setMore((list) => list.map((c) => (c.id === card.id ? { ...c, is_bookmarked: !next } : c)));
      setAlert(getApiError(err)?.message ?? getApiErrorMessage(err));
    }
  };

  const total = result?.recommended_total ?? 0;
  const loaded = [...(result?.recommended ?? []), ...more];
  const shown = result?.locked ? loaded : loaded.slice(0, visible);
  const canMore = total > PAGE;
  // [더보기]: 다음 10개가 아직 안 왔으면 서버 쪽 결과에서 이어 받아요(검색은 다시 안 해요). [접기]: 처음 10개만 보이게
  const loadMore = async () => {
    const next = visible + PAGE;
    const need = Math.min(next, total) - loaded.length;
    if (need > 0) {
      moreRequest.current?.abort();
      const controller = new AbortController();
      moreRequest.current = controller;
      setLoadingMore(true);
      try {
        const page = await searchAgents(query, { offset: loaded.length, limit: need, signal: controller.signal });
        // Aborted = the conditions changed meanwhile: these rows belong to the previous filter, drop them
        if (controller.signal.aborted) return;
        setMore((list) => [...list, ...page.recommended]);
      } catch (err) {
        if (!controller.signal.aborted) setAlert(getApiErrorMessage(err));
        return;
      } finally {
        if (!controller.signal.aborted) setLoadingMore(false);
      }
    }
    setVisible(next);
  };
  const collapse = () => setVisible(PAGE);

  const sortOptions: { value: FinderSort; label: string }[] = [
    { value: "relevance", label: query.q ? "유사도 순" : "추천 순" },
    { value: "latest", label: "최신순" },
  ];
  const empty = !isGuide && !loading && result && total === 0;

  return (
    <div className="mx-auto w-full max-w-[1440px] px-5 xl:px-[120px]">
      <section aria-label="검색 · 필터" className="flex flex-col gap-4 pb-6 pt-8">
        <NvSearchBar value={draft} onChange={setDraft} onSubmit={submit} placeholder={FINDER_PLACEHOLDER} className="min-h-[66px]" />

        {isGuide ? (
          <SuggestedTerms terms={suggestions} onPick={submit} />
        ) : isAll ? null : result?.search_mode === "keyword_fallback" ? (
          <InterpretationBar items={[]} onRemove={removeChip} notice={FALLBACK_NOTICE} />
        ) : (
          <InterpretationBar items={chips} onRemove={removeChip} />
        )}

        {facets && (
          <div className="flex flex-col gap-10 lg:flex-row">
            <div className="flex min-w-0 flex-1 flex-col gap-3 lg:max-w-[900px]">
              <FilterRow label="산업" facets={facets.industry} selected={query.industry} onToggle={(c) => toggleList("industry", c)} showCounts={isAll} />
              {facets.industry_detail.length > 0 && (
                <FilterRow label="└ 세부" facets={facets.industry_detail} selected={query.industry_detail} onToggle={(c) => toggleList("industry_detail", c)} showCounts={isAll} />
              )}
              <FilterRow label="업무" facets={facets.value_chain} selected={query.value_chain} onToggle={(c) => toggleList("value_chain", c)} showCounts={isAll} />
            </div>
            <div className="flex w-[260px] shrink-0 flex-col gap-1 lg:ml-auto">
              {TOGGLES.map((t) => (
                <NvToggleRow
                  key={t.key}
                  className="w-[240px]"
                  label={
                    <>
                      {t.label}
                      {isAll && <span className="ml-1 font-num text-[12px] leading-none text-nv-text-secondary">{facets.toggles[t.key]}</span>}
                    </>
                  }
                  checked={query[t.key]}
                  // 0건 토글은 켜 봐야 빈 화면이라 비활성 (제안 16 "0건 칩 · 토글은 비활성")
                  disabled={!query[t.key] && facets.toggles[t.key] === 0}
                  onCheckedChange={(on) => apply({ [t.key]: on })}
                />
              ))}
            </div>
          </div>
        )}

        <SelectedBar chips={selectedChips} onReset={resetAll} />
      </section>

      <div className="flex flex-col gap-6 pb-20 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 lg:max-w-[796px]">
          <div className="flex h-[38px] items-end justify-between">
            <h2 className="text-[18px] font-medium leading-[22px] text-nv-text-primary">
              {isGuide ? (
                "에이전트 찾기 시작하기"
              ) : (
                <>
                  {isAll ? "전체 에이전트" : "추천 에이전트"}
                  {result && !empty && <span className="ml-2 font-num">{total.toLocaleString("ko-KR")}</span>}
                </>
              )}
            </h2>
            {isGuide ? null : empty ? (
              <span className="font-num text-[18px] font-medium leading-[22px] text-nv-text-secondary">0</span>
            ) : (
              <SortMenu value={query.sort} options={sortOptions} onChange={(sort) => apply({ sort })} />
            )}
          </div>

          {isGuide ? (
            <GuideStart onSearch={submit} />
          ) : (
            <div className="flex flex-col gap-[14px] pt-[14px]">
              {error && <NvErrorState variant="plain" message={error} onRetry={() => setAttempt((n) => n + 1)} />}
              {loading && !result && [0, 1].map((i) => <NvFinderCardSkeleton key={i} />)}
              {result && (
                <LockedArea
                  locked={result.locked}
                  onBlocked={block}
                  prompt={accessLevel === "guest" ? { text: "로그인하면 추천 결과를 볼 수 있어요", position: 180, onLogin: goLogin } : undefined}
                  className={loading ? "opacity-60" : undefined}
                >
                  <div className="flex flex-col gap-[14px]">
                    {empty && (
                      <EmptyResult
                        onReset={resetFilters}
                        assistantPath={NV_PATHS.aiAssistant}
                        suggestions={result.relax_suggestions ?? []}
                        isAiCondition={(s) => s.code !== null && aiCodes.has(s.code)}
                        onRelax={relax}
                      />
                    )}
                    {shown.map((card) => (
                      <ResultCard key={card.id} card={card} onBookmark={toggleBookmark} />
                    ))}
                    {canMore && (
                      <MoreButton
                        shown={result.locked ? PAGE : Math.min(visible, total)}
                        total={total}
                        onMore={loadMore}
                        onCollapse={collapse}
                        disabled={loadingMore || loading}
                        label={isAll ? "전체 에이전트" : "추천 에이전트"}
                      />
                    )}
                    {result.similar.length > 0 && (
                      <>
                        {/* 시안: 유사 에이전트가 둘 이상일 때만 타이틀 옆에 개수 (847:51770 · 847:52187 "3", 하나일 땐 없음) */}
                        <SectionTitle
                          title="유사 에이전트"
                          count={result.similar_total > 1 ? result.similar_total : undefined}
                          note="유사도 기준점 이하 결과 · 유사도 높은 순"
                        />
                        {result.similar.map((card) => (
                          <ResultCard key={card.id} card={card} onBookmark={toggleBookmark} />
                        ))}
                      </>
                    )}
                  </div>
                </LockedArea>
              )}
            </div>
          )}
        </div>

        <div className="w-full shrink-0 lg:mt-[52px] lg:w-[380px]">
          <TrendingPanel onBlocked={block} />
        </div>
      </div>

      <NvAccessDialog {...gate.dialogProps} />
      <NvAlertDialog
        open={alert !== null}
        onOpenChange={(open) => !open && setAlert(null)}
        message={alert ?? ""}
        actions={<NvButton onClick={() => setAlert(null)}>확인</NvButton>}
      />
    </div>
  );
}
